import React from "react";
import { api, thumbUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatPrice } from "./shared";
import { PhotoFilterBar, BulkEditBar } from "./BulkEditBar";

function ScanNumbersCard({ events }) {
  const [eventId, setEventId] = React.useState("");
  const [force, setForce] = React.useState(false);
  const [job, setJob] = React.useState(null);
  const pollRef = React.useRef(null);

  React.useEffect(() => () => clearInterval(pollRef.current), []);

  const poll = (jobId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/scan-numbers/jobs/${jobId}`);
        setJob(r.data);
        if (["done", "failed"].includes(r.data.status)) {
          clearInterval(pollRef.current);
          if (r.data.status === "done") {
            toast.success(`Scan complete — numbers found in ${r.data.with_numbers} of ${r.data.done} photos`);
          } else {
            toast.error("Scan failed — check again later");
          }
        }
      } catch { /* keep polling */ }
    }, 2500);
  };

  const start = async () => {
    if (!eventId) return;
    try {
      const r = await api.post(`/scan-numbers/events/${eventId}?force=${force}`);
      if (!r.data.job_id) {
        toast.info(r.data.message || "Nothing to scan");
        return;
      }
      setJob({ id: r.data.job_id, status: "queued", total: r.data.total, done: 0 });
      toast.success(`Scanning ${r.data.total} photos in the background…`);
      poll(r.data.job_id);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const running = job && !["done", "failed"].includes(job.status);

  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-5 mb-8" data-testid="scan-numbers-card">
      <p className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold">Race number scan</p>
      <p className="text-xs text-white/50 mt-1 font-condensed">
        Reads race numbers off your photos (free, runs on the server) so riders can search themselves.
        New uploads are scanned automatically — use this for existing photos. You can correct any photo's numbers below.
      </p>
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <select
          value={eventId}
          onChange={(e) => setEventId(e.target.value)}
          className="input-minimal bg-transparent text-sm py-2 min-w-[220px]"
          data-testid="scan-event-select"
        >
          <option value="" className="bg-black">Select event…</option>
          {events.map((ev) => (
            <option key={ev.id} value={ev.id} className="bg-black">{ev.date} · {ev.track}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-xs text-white/50 font-condensed">
          <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="scan-force-checkbox" />
          Re-scan already scanned
        </label>
        <button
          onClick={start}
          disabled={!eventId || running}
          className="border border-white/25 px-5 py-2 text-xs uppercase tracking-[0.25em] hover:border-[var(--pm-orange)] disabled:opacity-50"
          data-testid="scan-start-button"
        >
          {running ? "Scanning…" : "Scan for race numbers"}
        </button>
      </div>
      {job && (
        <div className="mt-4" data-testid="scan-progress">
          <div className="h-1.5 bg-white/10">
            <div
              className="h-full bg-[var(--pm-orange)] transition-all"
              style={{ width: `${job.total ? Math.round(((job.done || 0) / job.total) * 100) : 0}%` }}
            />
          </div>
          <p className="text-xs text-white/50 mt-2 font-condensed">
            {job.status === "done"
              ? `Done — numbers found in ${job.with_numbers || 0} of ${job.done} photos`
              : job.status === "failed"
              ? "Scan failed"
              : `${job.done || 0} / ${job.total} photos scanned (≈${Math.max(1, Math.round(((job.total - (job.done || 0)) * 6) / 60))} min left)`}
          </p>
        </div>
      )}
    </div>
  );
}

function RaceNumbersEditor({ img, onChange }) {
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState("");
  const nums = img.race_numbers || [];

  const save = async () => {
    const parsed = value.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    try {
      const r = await api.put(`/images/${img.id}/race-numbers`, { race_numbers: parsed });
      toast.success(parsed.length ? `Tagged: ${r.data.race_numbers.map((n) => `#${n}`).join(" ")}` : "Numbers cleared");
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (editing) {
    return (
      <div className="flex gap-1 items-center" data-testid={`numbers-editor-${img.id}`}>
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
          placeholder="27, 114…"
          className="input-minimal text-xs py-1 flex-1"
          data-testid={`numbers-input-${img.id}`}
        />
        <button onClick={save} className="text-[10px] uppercase tracking-[0.15em] py-1 px-2 bg-white/10 hover:bg-[var(--pm-orange)] hover:text-black font-semibold" data-testid={`numbers-save-${img.id}`}>Save</button>
        <button onClick={() => setEditing(false)} className="text-[10px] py-1 px-1.5 text-white/50 hover:text-white">×</button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-1.5 items-center" data-testid={`numbers-display-${img.id}`}>
      <span className="text-[10px] uppercase tracking-[0.15em] text-white/40 mr-0.5">Race №</span>
      {nums.length > 0 ? (
        nums.map((n) => (
          <span key={n} className="text-[11px] font-display tracking-wider px-2 py-1 bg-[var(--pm-orange)]/15 pm-orange border border-[var(--pm-orange)]/30">#{n}</span>
        ))
      ) : (
        <span className="text-[10px] uppercase tracking-[0.15em] text-white/30">
          {img.numbers_scanned ? "none found" : "not scanned"}
        </span>
      )}
      <button
        onClick={() => { setValue(nums.join(", ")); setEditing(true); }}
        className="text-[10px] uppercase tracking-[0.15em] font-semibold border border-white/25 px-2.5 py-1 text-white/70 hover:border-[var(--pm-orange)] hover:text-[var(--pm-orange)] transition-colors"
        title="Add or correct race numbers"
        data-testid={`numbers-edit-${img.id}`}
      >
        {nums.length > 0 ? "Edit" : "+ Add numbers"}
      </button>
    </div>
  );
}


function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const now = new Date();
    const target = new Date(iso);
    return Math.round((target - now) / (1000 * 60 * 60 * 24));
  } catch { return null; }
}

function RetentionBadge({ image }) {
  if (image.is_active === false) {
    return <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-white/10 text-white/50 font-semibold">Removed</span>;
  }
  const state = image.retention_state || "active";
  if (state === "deleted") {
    return <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-red-900/40 text-red-300 font-semibold">Deleted</span>;
  }
  if (state === "expired") {
    return <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-yellow-500/20 text-yellow-300 font-semibold">Expired · Grace period</span>;
  }
  const days = daysUntil(image.expires_at);
  if (days === null) return null;
  if (days <= 5) {
    return <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-orange-500/25 pm-orange font-semibold">Expires in {days}d</span>;
  }
  return <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-white/5 text-white/60 font-semibold">Live · {days}d left</span>;
}

export default function PhotosList({ images: allImages, events, onChange }) {
  const [filterEvent, setFilterEvent] = React.useState("");
  const [filterGroup, setFilterGroup] = React.useState("");
  const [selectMode, setSelectMode] = React.useState(false);
  const [selected, setSelected] = React.useState(() => new Set());

  const images = allImages.filter((i) => (!filterEvent || i.event_id === filterEvent) && (!filterGroup || i.group_id === filterGroup));
  const editable = images.filter((i) => i.is_active !== false && i.retention_state !== "deleted");

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelected(new Set());
  const selectAll = () => setSelected(new Set(editable.map((i) => i.id)));
  const sel = { selectMode, selected, onToggle: toggle };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    try {
      await api.delete(`/admin/images/${id}`);
      toast.success("Removed");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const extend = async (id, days = 30) => {
    try {
      await api.post(`/admin/images/${id}/extend?days=${days}`);
      toast.success(`Extended by ${days} days`);
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const labelByImage = {};
  events.forEach((ev) => {
    (ev.groups || []).forEach((g) => {
      labelByImage[`${ev.id}:${g.id}`] = `${ev.date} · ${ev.track} · ${g.name}`;
    });
  });

  if (allImages.length === 0) return <p className="text-white/50 text-sm">No photos yet.</p>;

  // Group by lifecycle state so photographer sees urgent stuff first.
  // Inactive rows (soft-deleted by photographer/admin) go in Removed regardless of retention_state.
  const groups = {
    expiring: images.filter(i => i.is_active && i.retention_state === "active" && daysUntil(i.expires_at) !== null && daysUntil(i.expires_at) <= 5),
    active:   images.filter(i => i.is_active && i.retention_state === "active" && (daysUntil(i.expires_at) === null || daysUntil(i.expires_at) > 5)),
    expired:  images.filter(i => i.is_active && i.retention_state === "expired"),
    deleted:  images.filter(i => !i.is_active || i.retention_state === "deleted"),
  };

  return (
    <div className={`space-y-10 ${selected.size > 0 ? "pb-40" : ""}`} data-testid="admin-works-list">
      <ScanNumbersCard events={events} />
      <PhotoFilterBar
        events={events} eventId={filterEvent} groupId={filterGroup}
        onEvent={(v) => { setFilterEvent(v); setFilterGroup(""); }} onGroup={setFilterGroup}
        selectMode={selectMode} onToggleSelect={() => { setSelectMode((m) => !m); if (selectMode) clearSel(); }}
        shownCount={editable.length} onSelectAll={selectAll} onClear={clearSel} selectedCount={selected.size}
      />
      {images.length === 0 && <p className="text-white/50 text-sm" data-testid="photos-filter-empty">No photos match this filter.</p>}
      {groups.expiring.length > 0 && (
        <Section title={`Expiring soon (${groups.expiring.length})`} accent>
          <PhotoGrid images={groups.expiring} labelByImage={labelByImage} onRemove={remove} onExtend={extend} onChange={onChange} sel={sel} />
        </Section>
      )}
      {groups.expired.length > 0 && (
        <Section title={`Expired · Grace period (${groups.expired.length})`} accent>
          <p className="text-xs text-white/50 mb-3 font-condensed">Hidden from the storefront. Extend now to re-list — otherwise the original file will be permanently removed at the end of the grace period.</p>
          <PhotoGrid images={groups.expired} labelByImage={labelByImage} onRemove={remove} onExtend={extend} onChange={onChange} sel={sel} />
        </Section>
      )}
      {groups.active.length > 0 && (
        <Section title={`Live (${groups.active.length})`}>
          <PhotoGrid images={groups.active} labelByImage={labelByImage} onRemove={remove} onExtend={extend} onChange={onChange} sel={sel} />
        </Section>
      )}
      {groups.deleted.length > 0 && (
        <Section title={`Removed from storage (${groups.deleted.length})`}>
          <p className="text-xs text-white/50 mb-3 font-condensed">These photos&apos; original files have been permanently deleted. You&apos;d need to re-upload to sell them again.</p>
          <PhotoGrid images={groups.deleted} labelByImage={labelByImage} onRemove={remove} onExtend={extend} onChange={onChange} />
        </Section>
      )}
      <BulkEditBar selectedIds={[...selected]} events={events} onClear={clearSel} onDone={() => { clearSel(); onChange(); }} />
    </div>
  );
}

function Section({ title, children, accent }) {
  return (
    <div>
      <h3 className={`text-xs uppercase tracking-[0.3em] font-semibold mb-4 pb-3 border-b-2 ${accent ? "pm-orange border-[var(--pm-orange-40)]" : "text-white/60 border-white/10"}`}>{title}</h3>
      {children}
    </div>
  );
}

function PhotoGrid({ images, labelByImage, onRemove, onExtend, onChange, sel }) {
  const [limit, setLimit] = React.useState(24);
  const shown = images.slice(0, limit);
  const selecting = sel?.selectMode;
  return (
    <>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {shown.map((img) => {
        const label = labelByImage[`${img.event_id}:${img.group_id}`] || "Unassigned";
        const state = img.retention_state || "active";
        const canShowThumb = img.is_active !== false && state !== "deleted";
        const isSel = sel?.selected?.has(img.id);
        return (
          <div
            key={img.id}
            className={`border-2 bg-[#0a0a0a] transition-colors ${isSel ? "border-[var(--pm-orange)]" : "border-white/10"} ${(state === "deleted" || img.is_active === false) ? "opacity-50" : ""} ${selecting ? "cursor-pointer" : ""}`}
            onClick={selecting ? () => sel.onToggle(img.id) : undefined}
            data-testid={`photo-card-${img.id}`}
          >
            <div className="relative aspect-[4/5]">
              {canShowThumb ? (
                <img src={thumbUrl(img.id)} alt={img.title} loading="lazy" decoding="async" className="w-full h-full object-cover" onError={(e) => { e.target.style.opacity = "0.15"; }} />
              ) : (
                <div className="w-full h-full bg-[#111] flex items-center justify-center">
                  <span className="text-xs uppercase tracking-[0.3em] text-white/30">No preview</span>
                </div>
              )}
              <div className="absolute top-2 left-2"><RetentionBadge image={img} /></div>
              {selecting && (
                <div className={`absolute top-2 right-2 w-7 h-7 border-2 flex items-center justify-center ${isSel ? "bg-[var(--pm-orange)] border-[var(--pm-orange)] text-black" : "bg-black/60 border-white/60"}`} data-testid={`select-${img.id}`}>
                  {isSel && <span className="text-sm font-bold leading-none">✓</span>}
                </div>
              )}
            </div>
            <div className="p-4 space-y-2" onClick={selecting ? (e) => e.stopPropagation() : undefined}>
              <h3 className="font-display text-xl uppercase leading-tight">{img.title}</h3>
              <p className="text-[10px] text-white/40 uppercase tracking-[0.15em] font-condensed">{label}</p>
              <div className="text-xs text-white/60 flex justify-between font-condensed">
                <span>{formatPrice(img.price, img.currency)} · sold {img.sold_count || 0}</span>
                <span>Expires {fmtDate(img.expires_at)}</span>
              </div>
              <RaceNumbersEditor img={img} onChange={onChange} />
              <ExtendMenu img={img} state={state} onExtend={onExtend} onRemove={onRemove} />
            </div>
          </div>
        );
      })}
    </div>
    {images.length > limit && (
      <button
        onClick={() => setLimit((l) => l + 48)}
        className="mt-6 w-full border border-white/15 py-3 text-xs uppercase tracking-[0.25em] text-white/60 hover:border-[var(--pm-orange)] hover:text-white transition"
        data-testid="photos-show-more"
      >
        Show more ({images.length - limit} remaining)
      </button>
    )}
    </>
  );
}

function ExtendMenu({ img, state, onExtend, onRemove }) {
  const [open, setOpen] = React.useState(false);
  const [customDays, setCustomDays] = React.useState(60);
  if (state === "deleted" || !img.is_active) {
    return (
      <div className="flex gap-3 pt-2">
        {img.extension_count > 0 && (
          <span className="text-[10px] text-white/40 uppercase tracking-[0.15em]">
            {img.extension_count} extension{img.extension_count === 1 ? "" : "s"}
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="pt-2 space-y-2">
      {!open ? (
        <div className="flex gap-3 items-center">
          <button
            onClick={() => setOpen(true)}
            className="text-xs uppercase tracking-[0.2em] pm-orange hover:text-white font-semibold"
            data-testid={`extend-${img.id}`}
          >
            + Extend
          </button>
          <button
            onClick={() => onRemove(img.id)}
            className="text-xs uppercase tracking-[0.2em] text-red-400 hover:text-red-300 ml-auto"
            data-testid={`delete-${img.id}`}
          >
            Remove
          </button>
          {img.extension_count > 0 && (
            <span className="text-[10px] text-white/40 uppercase tracking-[0.15em]">
              ×{img.extension_count}
            </span>
          )}
        </div>
      ) : (
        <div className="border border-[var(--pm-orange-40)] p-2 bg-black/30 space-y-2">
          <div className="grid grid-cols-3 gap-1">
            <button
              onClick={() => { onExtend(img.id, 30); setOpen(false); }}
              className="text-[10px] uppercase tracking-[0.15em] py-1 border border-white/20 hover:border-[var(--pm-orange)] hover:pm-orange font-semibold"
              data-testid={`extend-30-${img.id}`}
            >+30d</button>
            <button
              onClick={() => { onExtend(img.id, 90); setOpen(false); }}
              className="text-[10px] uppercase tracking-[0.15em] py-1 border border-white/20 hover:border-[var(--pm-orange)] hover:pm-orange font-semibold relative"
              data-testid={`extend-90-${img.id}`}
            >+90d<span className="absolute -top-1 -right-1 bg-[var(--pm-orange)] text-black text-[8px] px-1 leading-none py-0.5">SAVE</span></button>
            <button
              onClick={() => { onExtend(img.id, 365); setOpen(false); }}
              className="text-[10px] uppercase tracking-[0.15em] py-1 border border-white/20 hover:border-[var(--pm-orange)] hover:pm-orange font-semibold relative"
              data-testid={`extend-365-${img.id}`}
            >+1yr<span className="absolute -top-1 -right-1 bg-[var(--pm-orange)] text-black text-[8px] px-1 leading-none py-0.5">BEST</span></button>
          </div>
          <div className="flex gap-1">
            <input
              type="number"
              min="1"
              max="3650"
              value={customDays}
              onChange={(e) => setCustomDays(Number(e.target.value) || 1)}
              className="input-minimal text-xs py-1 flex-1"
              data-testid={`extend-custom-input-${img.id}`}
            />
            <button
              onClick={() => { onExtend(img.id, customDays); setOpen(false); }}
              className="text-[10px] uppercase tracking-[0.15em] py-1 px-2 bg-white/10 hover:bg-[var(--pm-orange)] hover:text-black font-semibold"
              data-testid={`extend-custom-${img.id}`}
            >Go</button>
            <button
              onClick={() => setOpen(false)}
              className="text-[10px] uppercase tracking-[0.15em] py-1 px-2 text-white/50 hover:text-white"
              data-testid={`extend-close-${img.id}`}
            >×</button>
          </div>
        </div>
      )}
    </div>
  );
}
